import { SuggestionDataItem } from 'react-mentions';
import crc32 from 'crc-32';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import {
  AiMentionType,
  AIWebPageDetails,
  MenuItemType,
  MenusState,
  MenuType,
  onAttachmentFullScreenChangeParams,
} from '@nx-frontend/shared/types';
import { parseMenuItemId } from '@nx-frontend/shared/utils';
import { ChatbotContextValue } from './chatbot-context';
import {
  AiMentionItem,
  ChatbotCitation,
  ChatbotMessage,
  ChatbotThreadMessageRaw,
  AiMentionsResponse,
  MessageChunk,
  MessageChunkType,
  PromptType,
  ChatbotCitationDocument,
  UserLocationType,
  UserLocation,
} from './types';

const MESSAGE_END_STRING = '[FULL]{}[!FULLEND]';
export const SYSTEM_THREAD_ID = 'system';

export function b64DecodeUnicode(str: string) {
  // Going backwards: from bytestream, to percent-encoding, to original string.
  return decodeURIComponent(
    atob(str)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
}

interface CreateMessageParams {
  sessionId?: string | null;
  apiHost?: string;
  workspaceId: string;
  portalId?: string;
  /**
   * it works as a user identificator for portal visitors, if user is logged in it is ignored on the backend
   */
  portalVisitorId?: string;
  threadId: string | null;
  text: string | null;
  signal?: AbortSignal;
  promptType: PromptType;
  useWeb: boolean;
  currentLocationType: UserLocationType;
  currentLocationId?: string | null;
  currentLocationTitle?: string | null;
  enableDetectUserMessageContext?: boolean;
}

export const createMessage = async function* ({
  sessionId,
  apiHost,
  workspaceId,
  portalId,
  portalVisitorId,
  threadId,
  text,
  signal,
  promptType,
  useWeb,
  currentLocationType,
  currentLocationId,
  currentLocationTitle,
  enableDetectUserMessageContext,
}: CreateMessageParams) {
  const bodyThreadId = threadId === SYSTEM_THREAD_ID ? null : threadId;
  const baseUrl = `/ai-assistant/rest/workspaces/${workspaceId}/messages`;
  let url = apiHost ? `${apiHost}${baseUrl}` : baseUrl;

  const queryArgs = [];

  if (portalId) {
    queryArgs.push(`portalId=${encodeURIComponent(portalId)}`);
  }
  if (portalVisitorId) {
    queryArgs.push(`portalVisitorId=${encodeURIComponent(portalVisitorId)}`);
  }

  if (queryArgs.length) {
    url = `${url}?${queryArgs.join('&')}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionId ? { 'Everhelper-Session-Id': sessionId } : {}),
    },
    body: JSON.stringify({
      text,
      threadId: bodyThreadId,
      promptType,
      useWeb,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      currentLocationType,
      currentLocationId,
      currentLocationTitle,
      enableDetectUserMessageContext: Boolean(enableDetectUserMessageContext),
    }),
    signal,
  });

  for await (const item of handleResponseStream({
    response,
    threadId,
  })) {
    yield item;
  }
};

interface CreateAgentMessageParams {
  sessionId?: string | null;
  apiHost?: string;
  workspaceId: string;
  orgId: string;
  threadId: string;
  text: string | null;
  signal?: AbortSignal;
  /**
   * Array of tempStoredFile names
   */
  files: string[];
  webPageDetails?: AIWebPageDetails;
}

export const createAgentMessage = async function* ({
  sessionId,
  apiHost,
  orgId,
  workspaceId,
  threadId,
  text,
  signal,
  files = [],
  webPageDetails,
}: CreateAgentMessageParams) {
  const baseUrl = `/ai-assistant/rest/orgs/${orgId}/agent-messages?workspaceId=${encodeURIComponent(
    workspaceId
  )}&threadId=${encodeURIComponent(threadId)}`;
  const url = apiHost ? `${apiHost}${baseUrl}` : baseUrl;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionId ? { 'Everhelper-Session-Id': sessionId } : {}),
    },
    body: JSON.stringify({
      text,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      files,
      webPageDetails,
    }),
    signal,
  });

  for await (const item of handleResponseStream({
    response,
    threadId,
  })) {
    yield item;
  }
};

async function* handleResponseStream({ response, threadId }: { response: Response; threadId: string | null }) {
  if (response.status !== 200) {
    const json = await response.json();
    if (json.name === 'LimitError') {
      yield { type: MessageChunkType.THREAD, content: { id: threadId || SYSTEM_THREAD_ID } } as MessageChunk;
      yield { type: MessageChunkType.LIMIT_ERROR, content: { id: threadId || SYSTEM_THREAD_ID } } as MessageChunk;
      yield { type: MessageChunkType.DONE, content: MessageChunkType.DONE } as MessageChunk;
      return;
    }
    throw new Error(`Error occured, status ${response.status}`);
  }
  if (!response.body) {
    throw new Error(`Error occured, no body`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let isFullMessageResolved = false;

  // Read the stream
  let chunkBuffer = '';

  while (true) {
    let streamReadResponse: ReadableStreamReadResult<Uint8Array>;
    try {
      streamReadResponse = await reader.read();
    } catch (err) {
      console.error('Fail to read from AI assistant stream', err);
      break;
    }
    const { done, value } = streamReadResponse;
    if (done || isFullMessageResolved) {
      break;
    }

    // Process the chunk (value is a Uint8Array)
    // also decode base64
    const b64str = decoder.decode(value, { stream: true });
    /**
     * sign that we are in the middle of the full message
     * because the full message can be large it can be split into different streamed chunks by the server
     */
    chunkBuffer += b64str;
    if (!b64str.endsWith('\n') && !b64str.endsWith(MESSAGE_END_STRING)) {
      continue;
    }
    if (chunkBuffer !== '') {
      for (const line of chunkBuffer.split('\n')) {
        if (!line) continue;
        if (line === MESSAGE_END_STRING) {
          isFullMessageResolved = true;
          break;
        }
        try {
          const message = JSON.parse(line);
          if (message.t === 'error' && message.c.name === 'LimitError') {
            yield { type: MessageChunkType.THREAD, content: { id: threadId || SYSTEM_THREAD_ID } } as MessageChunk;
            yield { type: MessageChunkType.LIMIT_ERROR, content: { id: threadId || SYSTEM_THREAD_ID } } as MessageChunk;
            yield { type: MessageChunkType.DONE, content: MessageChunkType.DONE } as MessageChunk;
            continue;
          }

          yield {
            type: message.t,
            // new tools driven assistant do not uses base64 encoding
            content: message.c,
          } as MessageChunk;
        } catch (err) {
          console.log('Fail to parse', line, err);
          yield { type: MessageChunkType.ERROR, content: 'Unknown error' } as MessageChunk;
        }
      }
      chunkBuffer = '';
    }
  }

  yield { type: MessageChunkType.DONE, content: MessageChunkType.DONE } as MessageChunk;
}

function findCodeBlocks(markdownText: string) {
  const multiLineCodeBlockRegex = /```[\s\S]*?```/g;
  const inlineCodeBlockRegex = /`[^`]+`/g;

  const codeBlocks = [];
  let match;

  // Find multi-line code blocks
  while ((match = multiLineCodeBlockRegex.exec(markdownText)) !== null) {
    codeBlocks.push({
      start: match.index,
      end: match.index + match[0].length,
    });
  }

  // Find inline code blocks
  while ((match = inlineCodeBlockRegex.exec(markdownText)) !== null) {
    codeBlocks.push({
      start: match.index,
      end: match.index + match[0].length,
    });
  }

  return codeBlocks;
}

export function insertCitations(markdown: string, citations: ChatbotCitation[] | undefined): string | false {
  if (typeof markdown === 'string' && !markdown) {
    return false;
  }

  if (!citations || citations.length === 0) {
    return markdown;
  }

  if (typeof markdown !== 'string') {
    return markdown;
  }

  citations.sort((a, b) => a.start - b.start);

  const lines = markdown.split('\n');
  const codeBlocks = findCodeBlocks(markdown);
  const lineCitations: { [key: number]: ChatbotCitation[] } = {};

  // Helper function to check if a position is inside a code block
  function isInCodeBlock(position: number) {
    return codeBlocks.some((block) => position >= block.start && position <= block.end);
  }

  // Collect citations for each line, ignoring those inside code blocks
  citations.forEach((citation) => {
    if (isInCodeBlock(citation.start)) {
      return;
    }
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const lineStartPos = markdown.indexOf(line);
      const lineEndPos = lineStartPos + line.length;

      if (citation.start >= lineStartPos && citation.end <= lineEndPos) {
        if (!lineCitations[i]) {
          lineCitations[i] = [];
        }
        lineCitations[i].push(citation);
        break;
      }
    }
  });

  // Map over lines to insert citations, ensuring unique documentIds
  const result = lines.map((line, index) => {
    if (lineCitations[index]) {
      const uniqueDocumentIds = new Set<string>();
      const uniqueCitations = lineCitations[index].filter((citation) => {
        const docId = citation.documentIds[0];
        if (!uniqueDocumentIds.has(docId)) {
          uniqueDocumentIds.add(docId);
          return true;
        }
        return false;
      });
      const citationsText = uniqueCitations
        .map((citation) => `:blockquote[${citation.text}]{data-citation-id="${citation.id}"}`)
        .join(' ');
      return `${line} ${citationsText}`;
    }
    return line;
  });

  return result.join('\n');
}

export function mapToAiThreadMessages(messages: ChatbotThreadMessageRaw[]): ChatbotMessage[] {
  return messages.map((message) => {
    return {
      role: message.role,
      message: message.text,
      documents: message.documents,
      citations: message.citations,
      id: message.id,
      messageContext: message.messageContext,
      messageTransformed: message.textTransformed,
    };
  });
}

export function fixMarkdownAttachments(message: string) {
  return message.replace(/!\s+\[/gm, '![').replace(/:iframe\s+\[/gm, ':iframe[');
}

export function trimCitationDocumentAttachments(text: string) {
  let hasOneAttachment = false;
  return text
    .replace(/!\[.+\]\(.+\)/gm, (match) => {
      if (!hasOneAttachment) {
        hasOneAttachment = true;
        return match;
      }
      return '';
    })
    .replace(/:iframe\[.+\]\{.+\}/gm, (match) => {
      if (!hasOneAttachment) {
        hasOneAttachment = true;
        return match;
      }
      return '';
    })
    .trim();
}

export function mapToMentionList(data: AiMentionsResponse) {
  const res = Object.keys(data).reduce((mentions, category) => {
    const cat = category as keyof AiMentionsResponse;
    const items =
      data[cat]?.items?.map((item: AiMentionItem) => {
        let idString = `data-type="${item.target}" data-global-id="${item.target_id}"`;
        if ((item.target === AiMentionType.PORTAL_SECTION || item.target === AiMentionType.PORTAL_NOTE) && item.url) {
          idString += ` data-mention-url="${item.url}"`;
        }
        return {
          display: item.display_text,
          id: idString,
          type: item.target,
        };
      }) || [];

    return [...mentions, ...items] as SuggestionDataItem[];
  }, [] as SuggestionDataItem[]);
  return res as (SuggestionDataItem & { type: string })[];
}

export function getMentionIcon(
  params: SuggestionDataItem & {
    type: string;
  }
) {
  switch (params.type) {
    case AiMentionType.FOLDER:
    case AiMentionType.PORTAL_SECTION:
      return <Icon icon={IconsMap.FOLDER} className="w-4" />;
    case AiMentionType.NOTE:
    case AiMentionType.PORTAL_NOTE:
      return <Icon icon={IconsMap.NOTE} className="w-4" />;
    case 'workspace':
      return <Icon icon={IconsMap.CARD_GROUP} className="w-4" />;
    case 'portal':
      return <Icon icon={IconsMap.PORTAL_5} className="w-4" />;
  }
}

/**
 * Performs navigation to a cited document
 * @param doc
 * @param aiContext
 * @param currentPortalId
 */
export function navigateCitedDocument(
  doc: ChatbotCitationDocument,
  aiContext: ChatbotContextValue,
  currentPortalId: string | undefined
) {
  if (doc.workspaceId && doc.noteGlobalId) {
    if (doc.portalId) {
      if (
        currentPortalId === doc.portalId &&
        aiContext.portalSettings?.hasCustomizerAccess &&
        doc.type !== 'portalPage'
      ) {
        aiContext.onPageNavigate(AiMentionType.NOTE, doc.noteGlobalId);
      } else if (doc.portalUrl) {
        if (aiContext.portalId) {
          // we are in portal, navigate in the current page
          // FIXME use router for this
          // document.location.href = doc.portalUrl;
          let pushToUrl = doc.portalUrl;
          if (/^https?:\/\//i.test(pushToUrl)) {
            // remove domain part
            pushToUrl = pushToUrl.replace(/^https?:\/\/[^/]+/, '');
            if (!pushToUrl) {
              pushToUrl = '/';
            }
          }
          if (doc.blockId) {
            pushToUrl += '#hlblck-' + doc.blockId;
          }
          aiContext.onPageNavigate(AiMentionType.PORTAL_PAGE, doc.noteGlobalId, {
            routeUrl: pushToUrl,
          });
        } else {
          window.open(doc.portalUrl, '_blank');
        }
      } else {
        if (aiContext.portalSettings?.domain) {
          window.open(`https://${aiContext.portalSettings?.domain}`, '_blank');
        }
      }
    } else {
      aiContext.onPageNavigate(AiMentionType.NOTE, doc.noteGlobalId);
    }
  } else if (doc.url) {
    window.open(doc.url, '_blank');
  }
}

/**
 * Get information about user's current location in the web client
 * @param editorAttachmentFullscreen
 * @param menuState
 * @returns
 */
export function getCurrentLocation(
  editorAttachmentFullscreen: onAttachmentFullScreenChangeParams | null,
  menuState: MenusState,
  encodeCurrentLocation = true
): UserLocation {
  let currentLocationType: UserLocationType = MenuItemType.ALL_PAGES;
  let currentLocationId: string | null = null;
  let currentLocationTitle: string | null = null;

  if (editorAttachmentFullscreen) {
    currentLocationType = 'attachment';
    currentLocationId = editorAttachmentFullscreen.attachment.globalId;
    currentLocationTitle = editorAttachmentFullscreen.attachment.displayName;
  } else {
    if (
      menuState[MenuType.WEBNOTES_MENU].activeItemIds.length > 0 &&
      menuState[MenuType.WEBNOTES_MENU].activeItemIds[0]
    ) {
      const activeItemId = menuState[MenuType.WEBNOTES_MENU].activeItemIds[0];
      const activeItem = menuState[MenuType.WEBNOTES_MENU].entities[activeItemId];
      if (activeItem) {
        const parsedMenuItem = parseMenuItemId(activeItemId);
        currentLocationType = parsedMenuItem.type;
        currentLocationId = parsedMenuItem.id;
        currentLocationTitle = activeItem.name;
      }
    } else if (
      menuState[MenuType.PORTAL_SIDEBAR].activeItemIds.length > 0 &&
      menuState[MenuType.PORTAL_SIDEBAR].activeItemIds[0]
    ) {
      const activeItemId = menuState[MenuType.PORTAL_SIDEBAR].activeItemIds[0];
      const activeItem = menuState[MenuType.PORTAL_SIDEBAR].entities[activeItemId];
      if (activeItem) {
        if (activeItemId.indexOf('#') > 0) {
          const parsedMenuItem = parseMenuItemId(activeItemId);
          currentLocationType = parsedMenuItem.type;
          currentLocationId = parsedMenuItem.id;
          currentLocationTitle = activeItem.name;
        } else {
          currentLocationType = activeItem.type;
          if (activeItem.type === MenuItemType.NOTE) {
            currentLocationId = activeItem.targetId;
          } else if (activeItem.pageId) {
            currentLocationId = activeItem.pageId;
          } else {
            currentLocationId = activeItem.id;
          }
          currentLocationTitle = activeItem.name;
        }
      }
    }
  }
  let currentLocationIdEncoded = currentLocationId;
  if (encodeCurrentLocation) {
    const crc32_title = crc32.str(currentLocationTitle || '', 0) >>> 0;
    currentLocationIdEncoded = 'cle-' + Math.abs(crc32_title).toString() + '-' + currentLocationId;
  }

  return {
    type: currentLocationType,
    id: currentLocationIdEncoded,
    title: currentLocationTitle,
  };
}

/**
 * Checks if node is a text input node, meaning:
 * - input type="text"
 * - textarea
 * - content editable
 * @param node
 */
export function isTextInputNode(node: HTMLElement) {
  if (!node) {
    return false;
  }
  if (node.tagName === 'INPUT' && (node as HTMLInputElement).type === 'text') {
    return true;
  }
  if (node.tagName === 'TEXTAREA') {
    return true;
  }
  if (node.isContentEditable) {
    return true;
  }
  return false;
}
